import { useState, useEffect } from "react";
import PropTypes from 'prop-types';
import axios from "axios";
import { API } from "@/config";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { FileText } from "lucide-react";
import { db } from "../../db";
import { PhoneInput } from "@/components/PhoneInput";
import ImageUpload from "@/components/ImageUpload";
import NotificationSettings from "@/components/NotificationSettings";

export default function ClinicSettingsTab({ clinicId, token, clinic }) {
    const [isLoading, setIsLoading] = useState(false);
    const [clinicForm, setClinicForm] = useState({
        name: '', address: '', phone: '', email: ''
    });

    useEffect(() => {
        if (clinic) {
            setClinicForm({
                name: clinic.name,
                address: clinic.address || '',
                phone: clinic.phone || '',
                email: clinic.email || ''
            });
        }
    }, [clinic]);

    const handleUpdateClinic = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        try {
            await axios.put(`${API}/clinics/${clinicId}`, clinicForm, {
                headers: { Authorization: `Bearer ${token}` }
            });
            await db.clinics.update(clinicId, { ...clinicForm, updated_at: new Date().toISOString() });
            toast.success("Clinic updated!");
        } catch (error) {
            toast.error(error.response?.data?.detail || "Failed to update");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div style={{ maxWidth: '700px' }}>
            <div className="card" style={{ marginBottom: '1.5rem' }}>
                <h2 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1.5rem' }}>Clinic Settings</h2>
                <form onSubmit={handleUpdateClinic}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        <div>
                            <Label htmlFor="name">Clinic Name</Label>
                            <Input
                                id="name"
                                value={clinicForm.name}
                                onChange={(e) => setClinicForm({ ...clinicForm, name: e.target.value })}
                                required
                            />
                        </div>
                        <div>
                            <Label htmlFor="address">Address</Label>
                            <Input
                                id="address"
                                value={clinicForm.address}
                                onChange={(e) => setClinicForm({ ...clinicForm, address: e.target.value })}
                            />
                        </div>
                        <div>
                            <Label htmlFor="phone">Phone</Label>
                            <PhoneInput
                                id="phone"
                                value={clinicForm.phone}
                                onChange={(val) => setClinicForm({ ...clinicForm, phone: val })}
                            />
                        </div>
                        <div>
                            <Label htmlFor="email">Email</Label>
                            <Input
                                id="email"
                                type="email"
                                value={clinicForm.email}
                                onChange={(e) => setClinicForm({ ...clinicForm, email: e.target.value })}
                                placeholder="Enter clinic email"
                            />
                        </div>
                        <Button type="submit" disabled={isLoading} style={{ marginTop: '0.5rem' }}>
                            {isLoading ? 'Saving...' : 'Save Changes'}
                        </Button>
                    </div>
                </form>
            </div>

            {/* Notification Settings */}
            <div style={{ marginBottom: '1.5rem' }}>
                <NotificationSettings token={token} type="clinic" clinicId={clinicId} />
            </div>

            <div className="card">
                <h2 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <FileText size={20} />
                    Clinic Branding
                </h2>
                <p style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '1.5rem' }}>
                    Upload logo, stamp and signature to appear on generated invoices for this clinic.
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem' }}>
                    <ImageUpload
                        token={token}
                        uploadEndpoint={`/clinics/${clinicId}/upload-url`}
                        updateEndpoint={`/clinics/${clinicId}/images`}
                        downloadEndpoint={`/clinics/${clinicId}/download-url`}
                        imageType="logo"
                        currentUrl={clinic?.logo_url}
                        label="Logo"
                        onUploadComplete={(url) => {
                            if (clinic) {
                                db.clinics.update(clinicId, { logo_url: url });
                            }
                        }}
                    />
                    <ImageUpload
                        token={token}
                        uploadEndpoint={`/clinics/${clinicId}/upload-url`}
                        updateEndpoint={`/clinics/${clinicId}/images`}
                        downloadEndpoint={`/clinics/${clinicId}/download-url`}
                        imageType="stamp"
                        currentUrl={clinic?.stamp_url}
                        label="Stamp/Seal"
                        onUploadComplete={(url) => {
                            if (clinic) {
                                db.clinics.update(clinicId, { stamp_url: url });
                            }
                        }}
                    />
                    <ImageUpload
                        token={token}
                        uploadEndpoint={`/clinics/${clinicId}/upload-url`}
                        updateEndpoint={`/clinics/${clinicId}/images`}
                        downloadEndpoint={`/clinics/${clinicId}/download-url`}
                        imageType="sign"
                        currentUrl={clinic?.sign_url}
                        label="Signature"
                        onUploadComplete={(url) => {
                            if (clinic) {
                                db.clinics.update(clinicId, { sign_url: url });
                            }
                        }}
                    />
                </div>
            </div>
        </div>
    );
}

ClinicSettingsTab.propTypes = {
    clinicId: PropTypes.string.isRequired,
    token: PropTypes.string.isRequired,
    clinic: PropTypes.object,
};
